import {
  AccountCheckDto,
  IcpVersionDto,
  LeadEventDto,
  LeadPersonDto,
  OutreachMessageDto,
  SendingSettingsDto,
  SignalDto,
} from "@growth/dto";

/**
 * Sample data for the Leads draft UI. Institutions, people and addresses are made up (domains end
 * in .example, which can never receive mail). First Round's senders and settings mirror its real
 * setup so the screens can be judged. The ideal customer profile is real (server); the sample
 * keeps its own small ICP only because its made-up accounts were judged against it.
 */

export type SampleAccount = {
  id: string;
  name: string;
  segmentId: string;
  website: string;
  location: string;
  checks: AccountCheckDto[];
  signals: SignalDto[];
  teamInbox: string | null;
  phone: string | null;
  emailPattern: { pattern: string; basedOn: number } | null;
  status: "suggested" | "approved" | "held" | "excluded";
  statusNote: string;
  source: "research" | "register" | "csv" | "gateway" | "legacy";
  foundAt: string;
  researchCostUsd: number;
  /** The ICP version the checks were judged against. */
  icpVersion: number;
};

export type SamplePerson = {
  id: string;
  accountId: string;
  name: string;
  title: string;
  role: LeadPersonDto["role"];
  email: string | null;
  contactStatus: LeadPersonDto["contactStatus"];
  stage: LeadPersonDto["stage"];
  owner: string;
  phone: string | null;
  linkedin: LeadPersonDto["linkedin"];
  found: { url: string; quote: string; seenAt: string };
  events: LeadEventDto[];
  notes: string;
};

export type SampleMessage = Omit<
  OutreachMessageDto,
  "personName" | "accountName" | "to" | "engagement"
>;

export type SampleDb = {
  brandLabel: string;
  /** The ICP the sample accounts were judged against (not the brand's real one). */
  sampleIcp: {
    version: number;
    segments: IcpVersionDto["segments"];
    tiers: IcpVersionDto["tiers"];
  };
  accounts: SampleAccount[];
  people: SamplePerson[];
  messages: SampleMessage[];
  settings: SendingSettingsDto;
  /** What "Find more accounts" adds. */
  moreAccounts: SampleAccount[];
};

/** A time `days` ago at hh:mm local, as ISO. */
const at = (days: number, hour = 9, minute = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

let eventSeq = 0;
const event = (
  kind: LeadEventDto["kind"],
  when: string,
  detail: string,
  messageId: string | null = null,
  machineReason: string | null = null,
): LeadEventDto => ({
  id: `ev-${++eventSeq}`,
  at: when,
  kind,
  messageId,
  detail,
  machine: machineReason !== null,
  machineReason,
});

const check = (
  checkId: string,
  text: string,
  weight: number,
  verdict: AccountCheckDto["verdict"],
  quote = "",
  url: string | null = null,
  days = 6,
): AccountCheckDto => ({
  checkId,
  text,
  weight,
  verdict,
  quote,
  url,
  seenAt: verdict === "unknown" ? null : at(days),
});

// ─── First Round ─────────────────────────────────────────────────────────────────────────────

const FR_SEGMENTS: IcpVersionDto["segments"] = [
  {
    id: "seg-uni",
    name: "University careers services",
    summary:
      "Central careers and employability teams at Australian universities. A handful of advisers serve thousands of students, so most students never get a mock interview.",
    filters: [
      { label: "Country", value: "Australia" },
      { label: "Type", value: "University (TEQSA register)" },
      { label: "Has", value: "A central careers service" },
    ],
    checks: [
      {
        id: "u1",
        text: "Offers students interview preparation (workshops, mock interviews or appointments)",
        weight: 3,
        proof: "A careers page offering interview help to students",
      },
      {
        id: "u2",
        text: "Has a careers team of three or more named advisers",
        weight: 2,
        proof: "A staff or team page naming careers staff",
      },
      {
        id: "u3",
        text: "Already pays for student careers software",
        weight: 2,
        proof: "A link to, or mention of, a paid careers portal, CV checker or practice tool",
      },
      {
        id: "u4",
        text: "Serves 10,000 or more students",
        weight: 1,
        proof: "An enrolment figure on their site or a public register",
      },
      {
        id: "u5",
        text: "Publishes a careers team inbox or role addresses",
        weight: 1,
        proof: "An email address on a careers page",
      },
    ],
    signals: [
      "Hiring a careers or employability role",
      "Published a new employability strategy or framework",
      "Presenting at the NAGCAS conference",
    ],
    buyers: [
      { role: "budget_holder", titles: ["Director, Careers", "Head of Careers and Employability"] },
      {
        role: "champion",
        titles: ["Careers Manager", "Career Development Consultant", "Employability Manager"],
      },
      { role: "user", titles: ["Careers Adviser", "Careers Consultant"] },
    ],
    exclusions: [
      "Current customers, and their parent universities while a deal is open",
      "Accounts on hold",
      "Anyone on the suppression list",
    ],
  },
  {
    id: "seg-bs",
    name: "Business schools and MBA programs",
    summary:
      "Graduate business schools whose careers teams coach MBA students for competitive interviews. First Round's first customer is one of these.",
    filters: [
      { label: "Country", value: "Australia" },
      { label: "Type", value: "Business school or MBA program" },
    ],
    checks: [
      {
        id: "b1",
        text: "Has a dedicated careers or professional development team",
        weight: 3,
        proof: "A careers team page at the school (not only the parent university)",
      },
      {
        id: "b2",
        text: "Offers interview coaching or mock interviews",
        weight: 3,
        proof: "A page describing interview coaching for students",
      },
      {
        id: "b3",
        text: "Runs an MBA or postgraduate cohort intake",
        weight: 2,
        proof: "Program page with intake dates",
      },
      {
        id: "b4",
        text: "Publishes career outcomes (salary or employment rate)",
        weight: 1,
        proof: "An outcomes page or report",
      },
    ],
    signals: ["A cohort intake within eight weeks", "Careers team hiring"],
    buyers: [
      { role: "budget_holder", titles: ["Director, Careers and Professional Development"] },
      { role: "champion", titles: ["Career Development Consultant", "Careers Coach"] },
    ],
    exclusions: ["Current customers"],
  },
  {
    id: "seg-voc",
    name: "TAFE and private colleges",
    summary:
      "Vocational and private higher-education providers that sell job-ready outcomes but have small careers teams.",
    filters: [
      { label: "Country", value: "Australia" },
      { label: "Type", value: "TAFE or private provider (TEQSA or ASQA)" },
    ],
    checks: [
      {
        id: "v1",
        text: "Has a careers or student employment service",
        weight: 3,
        proof: "A student services page naming a careers or employment service",
      },
      {
        id: "v2",
        text: "Advertises job-ready or employability outcomes",
        weight: 2,
        proof: "Marketing copy promising employment outcomes",
      },
      {
        id: "v3",
        text: "Enrols international students",
        weight: 1,
        proof: "An international admissions page",
      },
    ],
    signals: ["Opening a new campus", "Hiring a student employment officer"],
    buyers: [
      { role: "budget_holder", titles: ["Head of Student Services"] },
      { role: "champion", titles: ["Student Employment Officer", "Careers Officer"] },
    ],
    exclusions: [],
  },
];

const TIERS = { A: 75, B: 55, C: 35 };

const U = FR_SEGMENTS[0]!.checks;
const B = FR_SEGMENTS[1]!.checks;
const V = FR_SEGMENTS[2]!.checks;
const c = (list: typeof U, index: number) => list[index]!;

const frAccounts = (): SampleAccount[] => [
  {
    id: "acc-triathlon",
    name: "University of Triathlon",
    segmentId: "seg-uni",
    website: "https://uni-triathlon.example",
    location: "Brisbane QLD",
    checks: [
      check(
        "u1",
        c(U, 0).text,
        3,
        "pass",
        "Book a mock interview with a careers consultant, or join our weekly interview skills workshop.",
        "https://uni-triathlon.example/careers/interviews",
      ),
      check(
        "u2",
        c(U, 1).text,
        2,
        "pass",
        "Our Careers and Employability team: 7 consultants across three campuses.",
        "https://uni-triathlon.example/careers/team",
      ),
      check("u3", c(U, 2).text, 2, "unknown"),
      check(
        "u4",
        c(U, 3).text,
        1,
        "pass",
        "More than 31,000 students study with us.",
        "https://uni-triathlon.example/about",
      ),
      check(
        "u5",
        c(U, 4).text,
        1,
        "pass",
        "Questions? careers@uni-triathlon.example",
        "https://uni-triathlon.example/careers/contact",
      ),
    ],
    signals: [
      {
        kind: "hiring",
        text: "Hiring an Employability Manager",
        url: "https://jobs.example/triathlon-employability-manager",
        seenAt: at(4),
      },
    ],
    teamInbox: "careers@uni-triathlon.example",
    phone: "07 5550 1200",
    emailPattern: { pattern: "first.last@uni-triathlon.example", basedOn: 4 },
    status: "approved",
    statusNote: "",
    source: "legacy",
    foundAt: at(20),
    researchCostUsd: 0.41,
    icpVersion: 2,
  },
  {
    id: "acc-harbourside",
    name: "Harbourside Business School",
    segmentId: "seg-bs",
    website: "https://harbourside-bs.example",
    location: "Sydney NSW",
    checks: [
      check(
        "b1",
        c(B, 0).text,
        3,
        "pass",
        "The Careers and Professional Development team works with every MBA student from week one.",
        "https://harbourside-bs.example/careers",
      ),
      check(
        "b2",
        c(B, 1).text,
        3,
        "pass",
        "One-to-one interview coaching, including case and behavioural interviews.",
        "https://harbourside-bs.example/careers/coaching",
      ),
      check(
        "b3",
        c(B, 2).text,
        2,
        "pass",
        "Next MBA intake: February.",
        "https://harbourside-bs.example/mba",
      ),
      check("b4", c(B, 3).text, 1, "unknown"),
    ],
    signals: [
      {
        kind: "news",
        text: "MBA intake starts in six weeks",
        url: "https://harbourside-bs.example/mba",
        seenAt: at(3),
      },
    ],
    teamInbox: "careers@harbourside-bs.example",
    phone: null,
    emailPattern: { pattern: "first.last@harbourside-bs.example", basedOn: 2 },
    status: "approved",
    statusNote: "",
    source: "legacy",
    foundAt: at(20),
    researchCostUsd: 0.38,
    icpVersion: 2,
  },
  {
    id: "acc-riverbend",
    name: "Riverbend University",
    segmentId: "seg-uni",
    website: "https://riverbend.example",
    location: "Perth WA",
    checks: [
      check(
        "u1",
        c(U, 0).text,
        3,
        "pass",
        "Practise your interview skills in a 30-minute mock interview with our careers team.",
        "https://riverbend.example/careers/mock-interviews",
      ),
      check(
        "u2",
        c(U, 1).text,
        2,
        "pass",
        "Meet the team: Daniel Moss (Manager), Aisha Rahman, and four careers consultants.",
        "https://riverbend.example/careers/team",
      ),
      check(
        "u3",
        c(U, 2).text,
        2,
        "fail",
        "Careers resources are free guides and our own workshop calendar.",
        "https://riverbend.example/careers/resources",
      ),
      check(
        "u4",
        c(U, 3).text,
        1,
        "pass",
        "Riverbend has 24,000 students.",
        "https://riverbend.example/about",
      ),
      check(
        "u5",
        c(U, 4).text,
        1,
        "pass",
        "careers.service@riverbend.example",
        "https://riverbend.example/careers/contact",
      ),
    ],
    signals: [],
    teamInbox: "careers.service@riverbend.example",
    phone: "08 5550 4400",
    emailPattern: { pattern: "first.last@riverbend.example", basedOn: 3 },
    status: "approved",
    statusNote: "",
    source: "legacy",
    foundAt: at(20),
    researchCostUsd: 0.44,
    icpVersion: 2,
  },
  {
    id: "acc-southern-cross",
    name: "Southern Cross Institute of Technology",
    segmentId: "seg-voc",
    website: "https://scit.example",
    location: "Darwin NT",
    checks: [
      check(
        "v1",
        c(V, 0).text,
        3,
        "pass",
        "Student Employment Services can help with resumes, interviews and finding work.",
        "https://scit.example/students/employment",
      ),
      check("v2", c(V, 1).text, 2, "unknown"),
      check(
        "v3",
        c(V, 2).text,
        1,
        "pass",
        "International students: apply for our 2027 intakes.",
        "https://scit.example/international",
      ),
    ],
    signals: [],
    teamInbox: null,
    phone: "08 5550 9000",
    emailPattern: null,
    status: "approved",
    statusNote: "",
    source: "legacy",
    foundAt: at(20),
    researchCostUsd: 0.36,
    icpVersion: 2,
  },
  {
    id: "acc-gumtree",
    name: "Gumtree Graduate School of Management",
    segmentId: "seg-bs",
    website: "https://gumtree-gsm.example",
    location: "Adelaide SA",
    checks: [
      check(
        "b1",
        c(B, 0).text,
        3,
        "pass",
        "Our careers coaches help you plan your next move.",
        "https://gumtree-gsm.example/careers",
        2,
      ),
      check("b2", c(B, 1).text, 3, "unknown"),
      check(
        "b3",
        c(B, 2).text,
        2,
        "pass",
        "MBA intakes in March and July.",
        "https://gumtree-gsm.example/mba",
        2,
      ),
      check(
        "b4",
        c(B, 3).text,
        1,
        "fail",
        "No outcomes page; the MBA brochure lists no employment figures.",
        "https://gumtree-gsm.example/mba",
        2,
      ),
    ],
    signals: [],
    teamInbox: null,
    phone: null,
    emailPattern: null,
    status: "suggested",
    statusNote: "",
    source: "research",
    foundAt: at(2),
    researchCostUsd: 0.4,
    icpVersion: 2,
  },
  {
    id: "acc-northgate",
    name: "Northgate University",
    segmentId: "seg-uni",
    website: "https://northgate.example",
    location: "Newcastle NSW",
    checks: [
      check(
        "u1",
        c(U, 0).text,
        3,
        "pass",
        "Interview workshops run every fortnight during semester.",
        "https://northgate.example/careers/events",
        1,
      ),
      check(
        "u2",
        c(U, 1).text,
        2,
        "pass",
        "Careers team: Ruth Ng (Careers Manager) and five careers educators.",
        "https://northgate.example/careers/about",
        1,
      ),
      check("u3", c(U, 2).text, 2, "unknown"),
      check("u4", c(U, 3).text, 1, "unknown"),
      check(
        "u5",
        c(U, 4).text,
        1,
        "pass",
        "Email careers@northgate.example",
        "https://northgate.example/careers/about",
        1,
      ),
    ],
    signals: [
      {
        kind: "news",
        text: "Published a new employability framework",
        url: "https://northgate.example/news/employability-framework",
        seenAt: at(9),
      },
    ],
    teamInbox: "careers@northgate.example",
    phone: null,
    emailPattern: null,
    status: "suggested",
    statusNote: "",
    source: "register",
    foundAt: at(1),
    researchCostUsd: 0.39,
    icpVersion: 2,
  },
  {
    id: "acc-coastal",
    name: "Coastal College of Commerce",
    segmentId: "seg-voc",
    website: "https://coastal-commerce.example",
    location: "Gold Coast QLD",
    checks: [
      check("v1", c(V, 0).text, 3, "unknown"),
      check(
        "v2",
        c(V, 1).text,
        2,
        "pass",
        "Graduate job-ready in 12 months.",
        "https://coastal-commerce.example",
        1,
      ),
      check(
        "v3",
        c(V, 2).text,
        1,
        "pass",
        "Study in Australia: international applications open.",
        "https://coastal-commerce.example/international",
        1,
      ),
    ],
    signals: [],
    teamInbox: null,
    phone: null,
    emailPattern: null,
    status: "suggested",
    statusNote: "",
    source: "register",
    foundAt: at(1),
    researchCostUsd: 0.22,
    icpVersion: 2,
  },
  {
    id: "acc-wattle",
    name: "Wattle Valley University",
    segmentId: "seg-uni",
    website: "https://wattle-valley.example",
    location: "Melbourne VIC",
    checks: [
      check(
        "u1",
        c(U, 0).text,
        3,
        "pass",
        "Mock interviews with Careers Online and in person.",
        "https://wattle-valley.example/careers",
      ),
      check(
        "u2",
        c(U, 1).text,
        2,
        "pass",
        "Careers and Employability: 14 staff.",
        "https://wattle-valley.example/careers/team",
      ),
      check(
        "u3",
        c(U, 2).text,
        2,
        "pass",
        "Use our careers portal to book and to check your CV.",
        "https://wattle-valley.example/careers/portal",
      ),
      check(
        "u4",
        c(U, 3).text,
        1,
        "pass",
        "More than 50,000 students.",
        "https://wattle-valley.example/about",
      ),
      check("u5", c(U, 4).text, 1, "unknown"),
    ],
    signals: [],
    teamInbox: null,
    phone: null,
    emailPattern: null,
    status: "held",
    statusNote: "Parent university of a current customer. Held until that deal settles.",
    source: "legacy",
    foundAt: at(20),
    researchCostUsd: 0.47,
    icpVersion: 2,
  },
  {
    id: "acc-lakeside",
    name: "Lakeside TAFE",
    segmentId: "seg-voc",
    website: "https://lakeside-tafe.example",
    location: "Hobart TAS",
    checks: [
      check(
        "v1",
        c(V, 0).text,
        3,
        "fail",
        "For careers advice, students are referred to the state careers service.",
        "https://lakeside-tafe.example/students",
      ),
      check("v2", c(V, 1).text, 2, "unknown"),
      check(
        "v3",
        c(V, 2).text,
        1,
        "fail",
        "Courses are open to domestic students only.",
        "https://lakeside-tafe.example/apply",
      ),
    ],
    signals: [],
    teamInbox: null,
    phone: null,
    emailPattern: null,
    status: "excluded",
    statusNote: "No careers service of its own.",
    source: "register",
    foundAt: at(20),
    researchCostUsd: 0.12,
    icpVersion: 2,
  },
];

const frMoreAccounts = (): SampleAccount[] => [
  {
    id: "acc-bayview",
    name: "Bayview Institute of Higher Education",
    segmentId: "seg-voc",
    website: "https://bayview-ihe.example",
    location: "Melbourne VIC",
    checks: [
      check(
        "v1",
        c(V, 0).text,
        3,
        "pass",
        "Our Careers Hub offers interview practice and job search help.",
        "https://bayview-ihe.example/careers-hub",
        0,
      ),
      check(
        "v2",
        c(V, 1).text,
        2,
        "pass",
        "92% of graduates employed within six months.",
        "https://bayview-ihe.example/outcomes",
        0,
      ),
      check(
        "v3",
        c(V, 2).text,
        1,
        "pass",
        "International students welcome.",
        "https://bayview-ihe.example/international",
        0,
      ),
    ],
    signals: [
      {
        kind: "hiring",
        text: "Hiring a Student Employment Officer",
        url: "https://jobs.example/bayview-seo",
        seenAt: at(0),
      },
    ],
    teamInbox: "careershub@bayview-ihe.example",
    phone: null,
    emailPattern: null,
    status: "suggested",
    statusNote: "",
    source: "research",
    foundAt: at(0),
    researchCostUsd: 0.43,
    icpVersion: 2,
  },
  {
    id: "acc-ironbark",
    name: "Ironbark University",
    segmentId: "seg-uni",
    website: "https://ironbark.example",
    location: "Toowoomba QLD",
    checks: [
      check(
        "u1",
        c(U, 0).text,
        3,
        "pass",
        "Mock interviews are available by appointment.",
        "https://ironbark.example/careers",
        0,
      ),
      check("u2", c(U, 1).text, 2, "unknown"),
      check("u3", c(U, 2).text, 2, "unknown"),
      check(
        "u4",
        c(U, 3).text,
        1,
        "pass",
        "Ironbark has 27,500 students.",
        "https://ironbark.example/about",
        0,
      ),
      check("u5", c(U, 4).text, 1, "unknown"),
    ],
    signals: [],
    teamInbox: null,
    phone: null,
    emailPattern: null,
    status: "suggested",
    statusNote: "",
    source: "research",
    foundAt: at(0),
    researchCostUsd: 0.37,
    icpVersion: 2,
  },
];

const linkedin = (
  url: string | null,
  sent: string | null = null,
  accepted: string | null = null,
) => ({
  url,
  requestSentAt: sent,
  acceptedAt: accepted,
});

const frPeople = (): SamplePerson[] => [
  {
    id: "p-bob",
    accountId: "acc-triathlon",
    name: "Bob Sample",
    title: "Careers Manager, Employability",
    role: "champion",
    email: "bob.sample@uni-triathlon.example",
    contactStatus: "published",
    stage: "engaged",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/bob-sample-example", at(5, 11)),
    found: {
      url: "https://uni-triathlon.example/careers/team",
      quote: "Bob Sample, Careers Manager (Employability) — bob.sample@uni-triathlon.example",
      seenAt: at(4),
    },
    events: [
      event("linkedin_sent", at(5, 11), "Connection request sent by Michael"),
      event("sent", at(3, 9, 2), "First email: “Mock interviews for 31,000 students”", "m-bob-1"),
      event("delivered", at(3, 9, 2), "Delivered", "m-bob-1"),
      event(
        "opened",
        at(3, 9, 2),
        "Opened",
        "m-bob-1",
        "Apple Mail privacy proxy loaded the images at delivery",
      ),
      event(
        "clicked",
        at(3, 9, 3),
        "Clicked 3 links",
        "m-bob-1",
        "Link scanner: three links in the same second, 41 s after delivery",
      ),
      event("opened", at(3, 14, 40), "Opened on a desktop mail app", "m-bob-1"),
      event("clicked", at(3, 14, 41), "Clicked the demo link", "m-bob-1"),
      event(
        "visited",
        at(3, 14, 41),
        "6 min on firstround.com.au, 4 pages, watched the demo",
        "m-bob-1",
      ),
    ],
    notes: "",
  },
  {
    id: "p-priya",
    accountId: "acc-triathlon",
    name: "Priya Natarajan",
    title: "Director, Careers and Employability",
    role: "budget_holder",
    email: "priya.natarajan@uni-triathlon.example",
    contactStatus: "published",
    stage: "contacted",
    owner: "Michael Buggy",
    phone: "07 5550 1201",
    linkedin: linkedin("https://www.linkedin.com/in/priya-natarajan-example"),
    found: {
      url: "https://uni-triathlon.example/careers/team",
      quote: "Professor Priya Natarajan, Director, Careers and Employability",
      seenAt: at(4),
    },
    events: [
      event("sent", at(3, 9, 5), "First email: “Interview practice at Triathlon”", "m-priya-1"),
      event("delivered", at(3, 9, 5), "Delivered", "m-priya-1"),
      event(
        "opened",
        at(3, 9, 5),
        "Opened",
        "m-priya-1",
        "Apple Mail privacy proxy loaded the images at delivery",
      ),
    ],
    notes: "",
  },
  {
    id: "p-tom",
    accountId: "acc-triathlon",
    name: "Tom Riley",
    title: "Careers Consultant",
    role: "user",
    email: "tom.riley@uni-triathlon.example",
    contactStatus: "guessed",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/tom-riley-example"),
    found: {
      url: "https://uni-triathlon.example/careers/events",
      quote: "Interview skills workshop, run by Tom Riley, Careers Consultant",
      seenAt: at(4),
    },
    events: [],
    notes: "",
  },
  {
    id: "p-sam",
    accountId: "acc-harbourside",
    name: "Sam Okafor",
    title: "Career Development Consultant",
    role: "champion",
    email: "sam.okafor@harbourside-bs.example",
    contactStatus: "published",
    stage: "replied",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/sam-okafor-example", at(9), at(8)),
    found: {
      url: "https://harbourside-bs.example/careers/team",
      quote: "Sam Okafor, Career Development Consultant — sam.okafor@harbourside-bs.example",
      seenAt: at(10),
    },
    events: [
      event("linkedin_sent", at(9, 10), "Connection request sent by Michael"),
      event("linkedin_accepted", at(8, 18), "Accepted"),
      event(
        "sent",
        at(8, 8, 30),
        "First email: “Practice interviews before the February intake”",
        "m-sam-1",
      ),
      event("delivered", at(8, 8, 30), "Delivered", "m-sam-1"),
      event("opened", at(8, 12, 15), "Opened", "m-sam-1"),
      event("clicked", at(8, 12, 16), "Clicked the demo link", "m-sam-1"),
      event(
        "visited",
        at(8, 12, 16),
        "11 min on firstround.com.au, tried the live demo",
        "m-sam-1",
      ),
      event(
        "replied",
        at(6, 16, 45),
        "“Happy to chat. Could you send a couple of times next week? Our intake starts soon.”",
        "m-sam-1",
      ),
    ],
    notes: "Wants to see it before the February intake.",
  },
  {
    id: "p-grace",
    accountId: "acc-harbourside",
    name: "Grace Liu",
    title: "Director, Careers and Professional Development",
    role: "budget_holder",
    email: "grace.liu@harbourside-bs.example",
    contactStatus: "pattern_verified",
    stage: "contacted",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/grace-liu-example", at(4, 10), at(2, 19)),
    found: {
      url: "https://harbourside-bs.example/about/leadership",
      quote: "Grace Liu, Director, Careers and Professional Development",
      seenAt: at(10),
    },
    events: [
      event("linkedin_sent", at(4, 10), "Connection request sent by Michael"),
      event("linkedin_accepted", at(2, 19), "Accepted"),
    ],
    notes: "",
  },
  {
    id: "p-daniel",
    accountId: "acc-riverbend",
    name: "Daniel Moss",
    title: "Manager, Careers Service",
    role: "champion",
    email: "daniel.moss@riverbend.example",
    contactStatus: "published",
    stage: "contacted",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin(null),
    found: {
      url: "https://riverbend.example/careers/team",
      quote: "Daniel Moss, Manager, Careers Service — daniel.moss@riverbend.example",
      seenAt: at(7),
    },
    events: [
      event(
        "sent",
        at(6, 9),
        "First email: “30-minute mock interviews, for every student”",
        "m-daniel-1",
      ),
      event("delivered", at(6, 9), "Delivered", "m-daniel-1"),
      event("opened", at(5, 13, 20), "Opened", "m-daniel-1"),
    ],
    notes: "",
  },
  {
    id: "p-aisha",
    accountId: "acc-riverbend",
    name: "Aisha Rahman",
    title: "Careers Consultant",
    role: "user",
    email: "aisha.rahman@riverbend.example",
    contactStatus: "published",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/aisha-rahman-example"),
    found: {
      url: "https://riverbend.example/careers/team",
      quote: "Aisha Rahman, Careers Consultant — aisha.rahman@riverbend.example",
      seenAt: at(7),
    },
    events: [],
    notes: "",
  },
  {
    id: "p-lee",
    accountId: "acc-southern-cross",
    name: "Lee Chen",
    title: "Student Employment Coordinator",
    role: "champion",
    email: "lee.chen@scit.example",
    contactStatus: "published",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin(null),
    found: {
      url: "https://scit.example/students/employment",
      quote: "Contact Lee Chen, Student Employment Coordinator, lee.chen@scit.example",
      seenAt: at(41),
    },
    events: [
      event("sent", at(2, 9, 10), "First email: “Interview practice for SCIT students”", "m-lee-1"),
      event("bounced", at(2, 9, 11), "Bounced: the mailbox doesn't exist any more", "m-lee-1"),
    ],
    notes: "",
  },
  {
    id: "p-maria",
    accountId: "acc-southern-cross",
    name: "Maria Gonzalez",
    title: "Head of Student Services",
    role: "budget_holder",
    email: null,
    contactStatus: "none",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin("https://www.linkedin.com/in/maria-gonzalez-example"),
    found: {
      url: "https://scit.example/about/leadership",
      quote: "Maria Gonzalez, Head of Student Services",
      seenAt: at(10),
    },
    events: [],
    notes: "",
  },
  {
    id: "p-hannah",
    accountId: "acc-gumtree",
    name: "Hannah Wells",
    title: "Careers Coach",
    role: "champion",
    email: "hannah.wells@gumtree-gsm.example",
    contactStatus: "published",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin(null),
    found: {
      url: "https://gumtree-gsm.example/careers",
      quote: "Book a session with Hannah Wells, Careers Coach (hannah.wells@gumtree-gsm.example)",
      seenAt: at(2),
    },
    events: [],
    notes: "",
  },
  {
    id: "p-oliver",
    accountId: "acc-wattle",
    name: "Oliver Grant",
    title: "Director, Careers",
    role: "budget_holder",
    email: "oliver.grant@wattle-valley.example",
    contactStatus: "published",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin(null),
    found: {
      url: "https://wattle-valley.example/careers/team",
      quote: "Oliver Grant, Director, Careers — oliver.grant@wattle-valley.example",
      seenAt: at(20),
    },
    events: [],
    notes: "",
  },
  {
    id: "p-ruth",
    accountId: "acc-northgate",
    name: "Ruth Ng",
    title: "Careers Manager",
    role: "champion",
    email: "ruth.ng@northgate.example",
    contactStatus: "published",
    stage: "new",
    owner: "Michael Buggy",
    phone: null,
    linkedin: linkedin(null),
    found: {
      url: "https://northgate.example/careers/about",
      quote: "Ruth Ng, Careers Manager — ruth.ng@northgate.example",
      seenAt: at(1),
    },
    events: [],
    notes: "",
  },
];

const sent = (
  id: string,
  personId: string,
  subject: string,
  body: string,
  sentAt: string,
  claimsUsed: string[] = ["A1", "A2", "A14", "A6"],
): SampleMessage => ({
  id,
  personId,
  senderId: "s-michael",
  kind: "first",
  subject,
  body,
  status: "sent",
  claimsUsed,
  createdAt: sentAt,
  sentAt,
  sentBy: "Michael Buggy",
  testMode: false,
  sendBackNote: null,
});

const frMessages = (): SampleMessage[] => [
  sent(
    "m-bob-1",
    "p-bob",
    "Mock interviews for 31,000 students",
    `Hi Bob,

I saw Triathlon's careers team runs mock interviews by appointment. Seven consultants and 31,000 students is a lot of appointments.

First Round lets students practise a job interview out loud with an AI interviewer that follows up when an answer is vague. Their feedback is ready when they hang up, so your consultants can spend their time on the students who need a person.

You can try a live interview yourself in about two minutes, no card needed: firstround.com.au

Worth a 20-minute look?`,
    at(3, 9, 2),
  ),
  sent(
    "m-priya-1",
    "p-priya",
    "Interview practice at Triathlon",
    `Hi Priya,

Your team offers mock interviews and a weekly workshop. I'm writing because most students who need the practice never book either.

First Round gives students a realistic practice interview with an AI interviewer, with feedback ready the moment they hang up. Students practise as often as they like; your consultants see them when it matters.

Could I show you in 20 minutes next week?`,
    at(3, 9, 5),
  ),
  sent(
    "m-sam-1",
    "p-sam",
    "Practice interviews before the February intake",
    `Hi Sam,

With the February MBA intake coming, I imagine your interview coaching calendar fills quickly.

First Round lets students practise out loud with an AI interviewer that digs deeper when an answer is vague, then gives feedback straight away. It's a way to make every coaching session start further along.

You can try it yourself in about two minutes: firstround.com.au

Happy to walk you through it.`,
    at(8, 8, 30),
  ),
  sent(
    "m-daniel-1",
    "p-daniel",
    "30-minute mock interviews, for every student",
    `Hi Daniel,

Riverbend's 30-minute mock interviews look excellent. The catch is that there are only so many of them.

First Round lets any student run a practice interview with an AI interviewer and get feedback as soon as they hang up. Your team keeps the appointments for the students who need them most.

Would a short demo be useful?`,
    at(6, 9),
  ),
  {
    ...sent(
      "m-lee-1",
      "p-lee",
      "Interview practice for SCIT students",
      `Hi Lee,

Student Employment Services helps SCIT students with interviews. First Round gives them a realistic practice interview with an AI interviewer whenever they want one, with feedback ready when they hang up.

Could I show you how it works?`,
      at(2, 9, 10),
    ),
    status: "bounced",
  },
  {
    id: "m-aisha-1",
    personId: "p-aisha",
    senderId: "s-michael",
    kind: "first",
    subject: "A practice interview for every Riverbend student",
    body: `Hi Aisha,

I saw you run Riverbend's mock interviews alongside Daniel. I've written to him too, but you're the one in the room with students.

First Round lets students practise out loud with an AI interviewer that follows up when an answer is vague. Their feedback is ready when they hang up, so they arrive at your mock interview having already practised.

Try a live interview yourself in about two minutes: firstround.com.au

Would it help your students before the end of semester?`,
    status: "draft",
    claimsUsed: ["A1", "A2", "A14", "A6"],
    createdAt: at(0, 8, 15),
    sentAt: null,
    sentBy: null,
    testMode: false,
    sendBackNote: null,
  },
  {
    id: "m-hannah-1",
    personId: "p-hannah",
    senderId: "s-michael",
    kind: "first",
    subject: "Gumtree MBA interviews, practised in advance",
    body: `Hi Hannah,

Gumtree's careers coaches help MBA students plan their next move, and interviews are usually the hardest part of that plan. First Round is an AI interview practice platform that lets students rehearse out loud with an interviewer that follows up and digs deeper, so that by the time they reach a coaching session with you they've already done the first few rounds of practice and know where their answers fall short. Feedback covers relevance, structure, pacing, confidence and filler words, and it's ready the moment they hang up.

We're an Australian company and we'd love to show you how it works.

Best,`,
    status: "sent_back",
    claimsUsed: ["A1", "A2", "A4", "A14", "A15"],
    createdAt: at(1, 15),
    sentAt: null,
    sentBy: null,
    testMode: false,
    sendBackNote: "Too long. Cut to about 90 words and end with one question.",
  },
];

const michael = {
  id: "s-michael",
  name: "Michael Buggy",
  title: "Operations Lead, First Round",
  email: "michael@firstround.com.au",
  signature: "Michael Buggy\nOperations Lead, First Round\nfirstround.com.au",
  optOutLine:
    "Not the right person, or rather not hear from me? Reply “no thanks” and I won't email again.",
  dailyCap: 30,
  sentToday: 0,
  domain: { name: "firstround.com.au", spf: "pass", dkim: "pass", dmarc: "pass" },
} as const;

const settings = (
  senders: SendingSettingsDto["senders"],
  suppression: SendingSettingsDto["suppression"] = [],
): SendingSettingsDto => ({
  provider: {
    kind: "ses",
    connected: false,
    detail: "Amazon SES is the planned provider. Until it's connected, sending runs in test mode.",
  },
  trackOpens: true,
  trackingDomain: null,
  scoring: { open: 10, click: 7, visit: null, reply: null },
  alerts: { reply: true, click: true, visit: true, weeklyListDay: "Monday" },
  followUpAfterDays: 5,
  senders,
  suppression,
});

const firstRound = (): SampleDb => ({
  brandLabel: "First Round",
  sampleIcp: { version: 2, segments: FR_SEGMENTS, tiers: TIERS },
  accounts: frAccounts(),
  people: frPeople(),
  messages: frMessages(),
  settings: settings(
    [
      { ...michael, domain: { ...michael.domain } },
      {
        id: "s-abhishek",
        name: "Abhishek Karmakar",
        title: "Founder, First Round",
        email: "abhishek@firstround.com.au",
        signature: "Abhishek Karmakar\nFounder, First Round\nfirstround.com.au",
        optOutLine: michael.optOutLine,
        dailyCap: 20,
        sentToday: 0,
        domain: { ...michael.domain },
      },
    ],
    [
      {
        email: "careers@lakeside-tafe.example",
        reason: "Asked not to be contacted",
        at: at(15),
      },
    ],
  ),
  moreAccounts: frMoreAccounts(),
});

// ─── Apex 25: the start of the journey ───────────────────────────────────────────────────────

const apex25 = (): SampleDb => ({
  brandLabel: "Apex 25",
  sampleIcp: { version: 0, segments: [], tiers: TIERS },
  accounts: [],
  people: [],
  messages: [],
  settings: settings([
    {
      id: "s-abhishek-ax",
      name: "Abhishek Karmakar",
      title: "Founder, Apex 25",
      email: "abhishek@tryapex25.com",
      signature: "Abhishek Karmakar\nFounder, Apex 25\ntryapex25.com",
      optOutLine: "Not for you? Reply “no thanks” and I won't email again.",
      dailyCap: 20,
      sentToday: 0,
      domain: { name: "tryapex25.com", spf: "pass", dkim: "pass", dmarc: "pass" },
    },
  ]),
  moreAccounts: [],
});

const blank = (brandLabel: string): SampleDb => ({
  brandLabel,
  sampleIcp: { version: 0, segments: [], tiers: TIERS },
  accounts: [],
  people: [],
  messages: [],
  settings: settings([]),
  moreAccounts: [],
});

/** The starting sample for a brand, picked by name. */
export const seedFor = (brandName: string): SampleDb => {
  if (/first\s*round/i.test(brandName)) return firstRound();
  if (/apex/i.test(brandName)) return apex25();
  return blank(brandName);
};
